const EU_EEA_COUNTRIES = [
  'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic',
  'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece',
  'Hungary', 'Iceland', 'Ireland', 'Italy', 'Latvia', 'Liechtenstein',
  'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Norway', 'Poland',
  'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden',
  'Switzerland', 'United Kingdom',
];

const HIPAA_COUNTRIES = ['United States', 'Canada'];

const LGPD_COUNTRIES = ['Brazil'];

// Asia-Pacific countries with PDPA-style laws
const PDPA_COUNTRIES = [
  'Singapore', 'Thailand', 'Malaysia', 'Philippines', 'Indonesia',
  'Vietnam', 'India', 'Japan', 'South Korea', 'Australia', 'New Zealand',
  'Hong Kong', 'Taiwan', 'Brunei', 'Sri Lanka', 'Bangladesh', 'Nepal',
  'Pakistan', 'Maldives',
];

const OTHER_COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina',
  'Armenia', 'Azerbaijan', 'Bahrain', 'Belarus',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Burkina Faso', 'Burundi', 'Cambodia',
  'Cameroon', 'Chile', 'China', 'Colombia', 'Congo', 'Costa Rica',
  'Cuba', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
  'Ethiopia', 'Fiji', 'Georgia', 'Ghana', 'Guatemala', 'Haiti', 'Honduras',
  'Iran', 'Iraq', 'Israel', 'Jamaica',
  'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Lebanon', 'Libya', 'Madagascar',
  'Mali', 'Mexico', 'Mongolia', 'Morocco', 'Mozambique', 'Myanmar',
  'Namibia', 'Nicaragua', 'Niger', 'Nigeria',
  'North Korea', 'Oman', 'Palestine', 'Panama', 'Papua New Guinea',
  'Paraguay', 'Peru', 'Qatar', 'Russia', 'Rwanda',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Somalia',
  'South Africa', 'Sudan', 'Syria',
  'Tajikistan', 'Tanzania', 'Tunisia', 'Turkey',
  'Turkmenistan', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'Uruguay', 'Uzbekistan', 'Venezuela',
  'Yemen', 'Zambia', 'Zimbabwe',
];

export const ALL_COUNTRIES = [
  ...EU_EEA_COUNTRIES,
  ...HIPAA_COUNTRIES,
  ...LGPD_COUNTRIES,
  ...PDPA_COUNTRIES,
  ...OTHER_COUNTRIES,
].sort((a, b) => a.localeCompare(b));

export const getConsentKeysForCountry = (country) => {
  if (EU_EEA_COUNTRIES.includes(country)) return ['gdpr'];
  if (country === 'United States') return ['hipaa_us'];
  if (country === 'Canada') return ['hipaa_ca'];
  if (LGPD_COUNTRIES.includes(country)) return ['lgpd'];
  if (PDPA_COUNTRIES.includes(country)) return ['pdpa'];
  return ['general'];
};

export const isEuCountry = (country) => EU_EEA_COUNTRIES.includes(country);

export const getRegionLabel = (country) => {
  if (EU_EEA_COUNTRIES.includes(country)) return 'EU / EEA (GDPR)';
  if (country === 'United States') return 'United States (HIPAA)';
  if (country === 'Canada') return 'Canada (Privacy Laws)';
  if (LGPD_COUNTRIES.includes(country)) return 'Brazil (LGPD)';
  if (PDPA_COUNTRIES.includes(country)) return 'Asia-Pacific (PDPA)';
  return 'International';
};

export const getNoticeTitleSuffix = (country) => {
  if (EU_EEA_COUNTRIES.includes(country)) return 'GDPR – Full Notice';
  if (country === 'United States') return 'HIPAA – Full Authorization';
  if (country === 'Canada') return 'Full Authorization';
  if (LGPD_COUNTRIES.includes(country)) return 'LGPD – Aviso Completo';
  if (PDPA_COUNTRIES.includes(country)) return 'Full Privacy Notice';
  return 'Full Privacy Notice';
};

export const consentTexts = {
  gdpr: {
    heading: 'EU / EEA (GDPR)',

    checkboxLabel: `Explicit consent (health data): I consent to the processing, use, transfer, and storage of my personal and medical data for the purpose of obtaining a second medical opinion. This may include sharing data with authorized healthcare professionals and secure service providers, including cross-border transfers with appropriate safeguards. I understand that I may withdraw my consent at any time as explained in the Privacy Notice.`,

    whyText: `Second-opinion services involve processing health data, which is considered a special category of personal data. Under GDPR (Art. 9(2)(a) and Art. 7), such processing requires explicit consent and clear withdrawal rights.`,

    title: 'EU / EEA (GDPR) – Full Notice',

    fullText: `Explicit Consent to Process Health Data for a Second Medical Opinion (GDPR)

Controller
The organization providing the second medical opinion service, including its authorized healthcare professionals and contracted data processors.

Purposes & Legal Basis
• To collect, store, review, transmit, and evaluate my personal and medical data (including demographic information, medical history, imaging, lab reports, diagnoses, medications, treating-physician notes, and communications) in order to provide a second medical opinion.
• Legal basis: Explicit consent for processing special-category (health) data under GDPR Art. 6(1)(a) and Art. 9(2)(a).

Recipients
• Licensed physicians and healthcare professionals engaged to provide the second opinion.
• Secure technology, hosting, imaging, transcription, and communication service providers operating under confidentiality and data processing agreements.

International Transfers
• Personal and medical data may be transferred outside the EEA where necessary to provide the service.
• Appropriate safeguards (such as Standard Contractual Clauses or equivalent mechanisms) will be applied where required.

Retention
• Data will be retained only for the period necessary to deliver the second opinion and for any legally required retention period consistent with medical and regulatory standards.

Your Rights
• Right of access, rectification, erasure, restriction, and data portability.
• Right to withdraw consent at any time without affecting processing carried out before withdrawal.
• Right to lodge a complaint with a supervisory authority.

Voluntary Provision
• Providing personal and medical data is voluntary.
• However, the second medical opinion service cannot be provided without the necessary information.

Children
• Where applicable law requires parental or guardian authorization for minors, consent must be provided or authorized by a parent or legal guardian.

Statement of Consent
By selecting "I agree," I give my explicit consent for the processing and transfer of my personal and medical data as described above for the sole purpose of obtaining a second medical opinion.`,
  },

  hipaa_us: {
    heading: 'United States (HIPAA)',

    checkboxLabel: `Authorization to use and disclose protected health information (PHI): I authorize Symbian Health and its affiliated healthcare professionals to use and disclose my protected health information, including medical records, imaging, lab results, and clinical notes, for the purpose of obtaining a second medical opinion. I understand I may revoke this authorization at any time in writing, and that revocation will not affect actions already taken in reliance on this authorization.`,

    whyText: `Under the U.S. Health Insurance Portability and Accountability Act (HIPAA), any use or disclosure of protected health information for purposes beyond treatment, payment, or operations requires your written authorization.`,

    title: 'United States (HIPAA) – Full Authorization',

    fullText: `Authorization for Use and Disclosure of Protected Health Information (HIPAA)

Description
This authorization permits Symbian Health and its contracted healthcare providers to use and disclose your protected health information (PHI) to deliver a second medical opinion service.

Who May Use or Disclose the Information
• Symbian Health and its affiliated licensed physicians and specialists.
• Contracted service providers (technology, imaging, secure communications) operating under Business Associate Agreements (BAAs) as required by HIPAA.

To Whom the Information May Be Disclosed
• Healthcare professionals assigned to review your case for a second opinion.
• Secure third-party platforms used to transmit, store, or process your medical records under HIPAA-compliant safeguards.

Purpose of Use or Disclosure
• To review, evaluate, and provide a second medical opinion based on your submitted health records and information.

Expiration
• This authorization will expire upon completion of the second opinion service or upon written revocation by you, whichever comes first.

Your Rights
• You have the right to revoke this authorization at any time by notifying Symbian Health in writing.
• Revocation is not effective for information already used or disclosed in reliance on this authorization.
• Treatment, payment, enrollment, or eligibility for benefits will not be conditioned on signing this authorization.
• Information disclosed pursuant to this authorization may be subject to re-disclosure by the recipient and may no longer be protected by HIPAA.

Voluntary Provision
• Signing this authorization is voluntary.
• However, without it, the second medical opinion service cannot be delivered.

Statement of Consent
By selecting "I agree," I authorize the use and disclosure of my protected health information as described above.`,
  },

  hipaa_ca: {
    heading: 'Canada (Privacy Laws)',

    checkboxLabel: `Authorization to use and disclose protected health information: I authorize Symbian Health and its affiliated healthcare professionals to use and disclose my protected health information, including medical records, imaging, lab results, and clinical notes, for the purpose of obtaining a second medical opinion. I understand I may revoke this authorization at any time in writing, and that revocation will not affect actions already taken in reliance on this authorization.`,

    whyText: `Under applicable Canadian provincial privacy laws, any use or disclosure of protected health information for purposes beyond treatment, payment, or operations requires your written authorization.`,

    title: 'Canada – Full Authorization',

    fullText: `Authorization for Use and Disclosure of Protected Health Information

Description
This authorization permits Symbian Health and its contracted healthcare providers to use and disclose your protected health information required to deliver a second medical opinion service.

Who May Use or Disclose the Information
• Symbian Health and its affiliated licensed physicians and specialists.
• Contracted service providers (technology, imaging, secure communications) complying with applicable Canadian provincial privacy laws.

To Whom the Information May Be Disclosed
• Healthcare professionals assigned to review your case for a second opinion.
• Secure third-party platforms used to transmit, store, or process your medical records under required privacy safeguards.

Purpose of Use or Disclosure
• To review, evaluate, and provide a second medical opinion based on your submitted health records and information.

Expiration
• This authorization will expire upon completion of the second opinion service or upon written revocation by you, whichever comes first.

Your Rights
• You have the right to revoke this authorization at any time by notifying Symbian Health in writing.
• Revocation is not effective for information already used or disclosed in reliance on this authorization.
• Treatment, payment, enrollment, or eligibility for benefits will not be conditioned on signing this authorization.

Voluntary Provision
• Signing this authorization is voluntary.
• However, without it, the second medical opinion service cannot be delivered.

Statement of Consent
By selecting "I agree," I authorize the use and disclosure of my protected health information as described above.`,
  },

  lgpd: {
    heading: 'Brasil (LGPD)',

    checkboxLabel: `Consentimento para tratamento de dados de saúde (dados sensíveis): Autorizo a Symbian Health e seus profissionais de saúde credenciados a coletar, armazenar, usar, transmitir e tratar meus dados pessoais e de saúde, incluindo histórico médico, exames e laudos, com a finalidade de obter uma segunda opinião médica. Estou ciente de que posso revogar este consentimento a qualquer momento, conforme previsto na Lei Geral de Proteção de Dados (LGPD – Lei nº 13.709/2018).`,

    whyText: `Nos termos da LGPD (Lei nº 13.709/2018), dados de saúde são classificados como dados sensíveis e exigem consentimento específico e destacado do titular para seu tratamento.`,

    title: 'Brasil (LGPD) – Aviso Completo',

    fullText: `Consentimento para Tratamento de Dados Pessoais de Saúde (LGPD)

Controller
Symbian Health e seus profissionais de saúde e fornecedores de serviços contratados, responsáveis pelo tratamento dos seus dados pessoais.

Purposes & Legal Basis
• Coletar, armazenar, transmitir e tratar seus dados pessoais e de saúde (incluindo histórico médico, exames de imagem, laudos laboratoriais, diagnósticos, medicações e comunicações) para fornecer uma segunda opinião médica.
• Base legal: Consentimento do titular para dados sensíveis, nos termos do Art. 11, I, e Art. 8º da LGPD.

Recipients
• Médicos e profissionais de saúde licenciados designados para analisar seu caso.
• Fornecedores de tecnologia, hospedagem segura, imagem, transcrição e comunicação, operando sob acordos de confidencialidade e tratamento de dados.

International Transfers
• Seus dados poderão ser transferidos internacionalmente quando necessário para a prestação do serviço, com as salvaguardas adequadas conforme exigido pela LGPD e regulamentações da ANPD.

Retention
• Os dados serão retidos pelo período necessário para a entrega da segunda opinião médica e conforme exigências legais e regulatórias aplicáveis.

Your Rights
• Direito de acesso, correção, anonimização, bloqueio ou eliminação de dados desnecessários.
• Direito de portabilidade e de revogar o consentimento a qualquer momento.
• Direito de peticionar à Autoridade Nacional de Proteção de Dados (ANPD).

Voluntary Provision
• O fornecimento dos dados é voluntário, porém indispensável para a prestação do serviço de segunda opinião médica.

Statement of Consent
Ao selecionar "Eu concordo", você dá seu consentimento explícito para o tratamento e transferência dos seus dados pessoais e de saúde conforme descrito acima.`,
  },

  pdpa: {
    heading: 'Asia-Pacific (Data Protection)',

    checkboxLabel: `Consent to collect, use and disclose personal data (including health information): I consent to Symbian Health and its authorized healthcare professionals collecting, using, storing, and disclosing my personal data, including health and medical information, for the purpose of obtaining a second medical opinion. This may include transferring my data to other countries where the service is operated. I understand I may withdraw my consent at any time, subject to legal or contractual restrictions.`,

    whyText: `Many Asia-Pacific jurisdictions — including Singapore (PDPA), Thailand (PDPA), India (DPDPA), Japan (APPI), Australia (Privacy Act), South Korea (PIPA), and others — require explicit consent for the collection, use, and cross-border transfer of personal and health data.`,

    title: 'Asia-Pacific (Data Protection) – Full Notice',

    fullText: `Consent to Collect, Use and Disclose Personal Data Including Health Information

Controller
Symbian Health and its authorized healthcare professionals and contracted service providers responsible for processing your personal data.

Purposes & Legal Basis
• To collect, store, use, transmit, and process your personal and health data (including medical history, imaging, lab results, diagnoses, medications, and communications) for the purpose of delivering a second medical opinion.
• Legal basis: Your explicit consent in accordance with applicable data protection legislation in your jurisdiction (e.g., Singapore PDPA, India DPDPA, Australia Privacy Act, Japan APPI, South Korea PIPA, Thailand PDPA).

Recipients
• Licensed physicians and healthcare professionals assigned to review your case.
• Contracted technology, hosting, imaging, transcription, and secure communication providers operating under data processing agreements and confidentiality obligations.

International Transfers
• Your personal and health data may be transferred to other countries in order to deliver the second medical opinion service.
• Such transfers will be made in accordance with applicable cross-border data transfer requirements, including standard contractual clauses or equivalent safeguards as required by law.

Retention
• Data will be retained for the period necessary to deliver the service and comply with applicable medical records and regulatory requirements.

Your Rights
• Right of access to your personal data held by us.
• Right to correct inaccurate or incomplete personal data.
• Right to withdraw consent at any time (subject to legal or contractual restrictions).
• Right to raise concerns or complaints with the relevant data protection authority in your jurisdiction.

Voluntary Provision
• Providing your personal and health data is voluntary.
• However, the second medical opinion service cannot be delivered without the necessary information.

Children
• Where your jurisdiction requires parental or guardian consent for minors, consent must be provided or authorized by a parent or legal guardian.

Statement of Consent
By selecting "I agree," I give my explicit consent for the collection, use, disclosure, and international transfer of my personal and health data as described above for the sole purpose of obtaining a second medical opinion.`,
  },

  general: {
    heading: 'International Privacy Consent',

    checkboxLabel: `Consent to process personal and health data: I consent to Symbian Health and its authorized healthcare professionals collecting, storing, using, transferring, and processing my personal and medical data — including medical history, imaging, laboratory results, diagnoses, medications, and clinical notes — for the sole purpose of obtaining a second medical opinion. I understand that my data may be transferred internationally and that I may withdraw this consent at any time.`,

    whyText: `To provide a second medical opinion, Symbian Health must process sensitive health information. We are committed to handling your data securely, confidentially, and in accordance with applicable local privacy requirements.`,

    title: 'International Privacy Consent – Full Notice',

    fullText: `Consent to Process Personal and Health Data for a Second Medical Opinion

Controller
Symbian Health and its licensed healthcare professionals and contracted service providers responsible for processing your personal data in connection with the second medical opinion service.

Purposes & Legal Basis
• To collect, store, review, transmit, and evaluate your personal and health data (including demographic information, medical history, imaging, lab reports, diagnoses, medications, treating-physician notes, and communications) in order to provide a second medical opinion.
• Legal basis: Your explicit consent to the processing of your personal and health data for this specific purpose.

Recipients
• Licensed physicians and healthcare specialists engaged to review your case and provide a second opinion.
• Contracted technology, hosting, imaging, transcription, and communication service providers operating under confidentiality agreements and data processing obligations.

International Transfers
• Your personal and health data may be transferred internationally as necessary to deliver the second medical opinion service.
• Symbian Health will ensure that appropriate safeguards are in place to protect your data during any such transfer, in accordance with applicable law.

Retention
• Your data will be retained for the minimum period required to deliver the second opinion service and to meet any applicable legal, medical, or regulatory retention obligations.

Your Rights
• Right to access your personal data held by Symbian Health.
• Right to correct or update inaccurate personal data.
• Right to withdraw this consent at any time; withdrawal will not affect processing already carried out before revocation.
• Right to raise concerns or complaints with the appropriate data protection or privacy authority in your country.

Voluntary Provision
• Providing your personal and health data is entirely voluntary.
• However, without the necessary information, the second medical opinion service cannot be delivered.

Children
• Where your country's laws require parental or guardian authorization for the processing of minors' data, this consent must be given or countersigned by a parent or legal guardian.

Security
• Symbian Health implements appropriate technical and organizational measures to protect your personal and health data against unauthorized access, disclosure, alteration, or destruction.

Statement of Consent
By selecting "I agree," I give my explicit consent for the collection, use, storage, disclosure, and international transfer of my personal and health data as described above for the sole purpose of obtaining a second medical opinion.`,
  },
};